import { useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@store/index';
import { selectIsAuthenticated, logout, sessionExpired, validateSession } from '@features/auth/authSlice';
import { showNotification } from '@features/ui/uiSlice';
import { SESSION_EXPIRED_EVENT } from '@services/api';

const SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes of inactivity
const WARNING_BEFORE_LOGOUT = 5 * 60 * 1000; // Warn 5 minutes before logout

export const useSessionManager = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  const lastActivityRef = useRef(Date.now());
  const warningShownRef = useRef(false);
  const sessionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Update last activity time
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    warningShownRef.current = false;
  }, []);

  // Handle logout due to inactivity
  const handleInactiveLogout = useCallback(async () => {
    dispatch(showNotification({
      message: t('session.expiredDueToInactivity'),
      type: 'warning',
      category: 'session',
    }));
    await dispatch(logout());
    navigate('/login');
  }, [dispatch, navigate, t]);

  // Show warning before logout
  const showInactivityWarning = useCallback(() => {
    if (!warningShownRef.current) {
      warningShownRef.current = true;
      dispatch(showNotification({
        message: t('session.expiresSoon'),
        type: 'warning',
        category: 'session',
      }));
    }
  }, [dispatch, t]);

  // Check for inactivity
  const checkInactivity = useCallback(() => {
    if (!isAuthenticated) return;

    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;

    if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
      handleInactiveLogout();
    } else if (timeSinceLastActivity >= INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT) {
      showInactivityWarning();

      // Set timer for final logout
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }

      const timeUntilLogout = INACTIVITY_TIMEOUT - timeSinceLastActivity;
      inactivityTimerRef.current = setTimeout(handleInactiveLogout, timeUntilLogout);
    }
  }, [isAuthenticated, handleInactiveLogout, showInactivityWarning]);

  // Validate session periodically
  const checkSession = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const isValid = await dispatch(validateSession()).unwrap();
      if (!isValid) {
        const publicPaths = ['/login', '/forgot-password', '/reset-password'];
        const isPublicRoute = publicPaths.some(path => window.location.pathname.startsWith(path));
        if (!isPublicRoute) {
          dispatch(showNotification({
            message: t('session.expired'),
            type: 'error',
            category: 'session',
          }));
          navigate('/login');
        }
      }
    } catch (error) {
      // Session validation failed, likely due to network error
      console.error('Session validation failed:', error);
    }
  }, [dispatch, navigate, isAuthenticated, t]);

  // Set up activity listeners
  useEffect(() => {
    if (!isAuthenticated) return;

    // Activity events to track
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Add activity listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    // Set up periodic checks
    sessionCheckIntervalRef.current = setInterval(() => {
      checkSession();
      checkInactivity();
    }, SESSION_CHECK_INTERVAL);

    // Initial checks
    checkSession();
    checkInactivity();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });

      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
      }

      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [isAuthenticated, updateActivity, checkSession, checkInactivity]);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated) {
        // Page is visible again, check session
        checkSession();
        updateActivity();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkSession, updateActivity, isAuthenticated]);

  // Handle API-level refresh failures inside the SPA so login can recover without a page reload.
  useEffect(() => {
    const handleSessionExpired = (event: Event) => {
      event.preventDefault();
      flushSync(() => {
        dispatch(sessionExpired());
      });
      navigate('/login', { replace: true });
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);

    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, [dispatch, navigate]);

  // Extend session (for manual action)
  const extendSession = useCallback(() => {
    updateActivity();
    dispatch(showNotification({
      message: t('session.extended'),
      type: 'success',
      category: 'session',
    }));
  }, [updateActivity, dispatch, t]);

  return {
    extendSession,
  };
};
